import React, { useState } from 'react';
import { usePIM } from '../context/PIMContext';
import { Input } from './ui/Input';
import { Settings, RefreshCw } from 'lucide-react';
import { PRODUCT_TYPES } from '../config';

import { ColorManagementModal } from './modals/ColorManagementModal';
import { CategoryManagementModal } from './modals/CategoryManagementModal';
import { StatusManagementModal } from './modals/StatusManagementModal';

export const ProductForm = () => {
    const { colors, categories, statuses, productData, setProductData, canEditContent, canManageSettings, currentUser } = usePIM();
    const [isColorModalOpen, setIsColorModalOpen] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);

    if (!productData) {
        console.error("ProductForm: productData is missing from context");
        return <div className="p-4 text-red-500">Error: Product Data not loaded</div>;
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProductData(prev => ({ ...prev, [name]: value }));
    };

    const handleTypeChange = (type) => {
        const currentTypes = productData.product_types || [];
        let newTypes;

        if (currentTypes.includes(type)) {
            newTypes = currentTypes.filter(t => t !== type);
        } else {
            newTypes = [...currentTypes, type];
        }

        // Logic: Component and Accessory are mutually exclusive
        if (type === 'component') {
            if (newTypes.includes('component')) {
                // If component added, remove accessory
                newTypes = newTypes.filter(t => t !== 'accessory');
            }
        }
        if (type === 'accessory') {
            if (newTypes.includes('component')) {
                // If accessory added (via type list), remove component?
                // Or block? User said: "Wenn component gewählt -> Accessory-Checkbox disabled + auf false setzen"
                // So if component is present, accessory cannot be added.
                // But in handleTypeChange, if we just toggled accessory...
                if (productData.product_types.includes('component')) return; // Prevent adding accessory
            }
        }

        // Sync logic with legacy isAccessory flag
        const isAcc = newTypes.includes('accessory');

        setProductData(prev => ({
            ...prev,
            product_types: newTypes,
            isAccessory: isAcc
        }));
    };

    return (
        <div className="w-full h-full flex flex-col gap-1">
            {/* MODEL */}
            <div className="space-y-0.5">
                <label className="text-xs font-medium text-stone-500 uppercase">MODEL:</label>
                <div className="flex gap-2">
                    <Input
                        name="model"
                        value={productData.model}
                        onChange={handleChange}
                        className="bg-stone-950 border-stone-700 py-1.5 flex-1"
                        disabled={!canEditContent}
                    />

                </div>
            </div>

            {/* SKU */}
            <div className="space-y-0.5">
                <label className="text-xs font-medium text-stone-500 uppercase">SKU*:</label>
                <Input
                    name="sku"
                    value={productData.sku}
                    onChange={handleChange}
                    className="bg-stone-950 border-stone-700 py-1.5"
                    disabled={!canEditContent}
                />
            </div>

            {/* EAN */}
            <div className="space-y-0.5">
                <label className="text-xs font-medium text-stone-500 uppercase">EAN (GTIN):</label>
                <Input
                    name="ean"
                    value={productData.ean}
                    onChange={handleChange}
                    className="bg-stone-950 border-stone-700 py-1.5"
                    disabled={!canEditContent}
                />
            </div>

            {/* COLOR */}
            <div className="space-y-0.5">
                <label className="text-xs font-medium text-stone-500 uppercase">COLOR:</label>
                <div className="flex gap-2">
                    <select
                        name="color"
                        value={productData.color}
                        onChange={handleChange}
                        disabled={!canEditContent}
                        className="flex-1 bg-stone-950 border border-stone-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-600 appearance-none"
                    >
                        <option value="">Select Color</option>
                        {colors.map(c => (
                            <option key={c.id || c} value={c.name || c}>
                                {c.name || c}{c.pantone ? ` - ${c.pantone}` : ''}
                            </option>
                        ))}
                    </select>
                    {canManageSettings && (
                        <button
                            onClick={() => setIsColorModalOpen(true)}
                            className="p-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition"
                        >
                            <Settings size={16} />
                        </button>
                    )}
                </div>
            </div>
            <ColorManagementModal isOpen={isColorModalOpen} onClose={() => setIsColorModalOpen(false)} />

            {/* CATEGORY */}
            <div className="space-y-0.5">
                <label className="text-xs font-medium text-stone-500 uppercase">CATEGORY:</label>
                <div className="flex gap-2">
                    <select
                        name="category"
                        value={productData.category}
                        onChange={handleChange}
                        disabled={!canEditContent}
                        className="flex-1 bg-stone-950 border border-stone-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-600 appearance-none"
                    >
                        <option value="">Select Category</option>
                        {categories.map(c => (
                            <option key={c.id || c} value={c.name || c}>
                                {c.name || c}
                            </option>
                        ))}
                    </select>
                    {canManageSettings && (
                        <button
                            onClick={() => setIsCategoryModalOpen(true)}
                            className="p-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition"
                        >
                            <Settings size={16} />
                        </button>
                    )}
                </div>
            </div>
            <CategoryManagementModal isOpen={isCategoryModalOpen} onClose={() => setIsCategoryModalOpen(false)} />

            {/* STATUS */}
            <div className="space-y-0.5">
                <label className="text-xs font-medium text-stone-500 uppercase">STATUS:</label>
                <div className="flex gap-2">
                    <select
                        name="status"
                        value={productData.status}
                        onChange={handleChange}
                        style={{
                            borderColor: statuses.find(s => s.name === productData.status)?.hex_code || '',
                            backgroundColor: statuses.find(s => s.name === productData.status)?.hex_code ? `${statuses.find(s => s.name === productData.status)?.hex_code}20` : ''
                        }}
                        className="flex-1 bg-stone-950 border border-stone-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-blue-600 appearance-none transition-colors"
                        disabled={!canEditContent}
                    >
                        <option value="" style={{ backgroundColor: '#0c0a09' }}>Select Status</option>
                        {statuses.map(s => (
                            <option key={s.id || s} value={s.name || s} style={{ backgroundColor: '#0c0a09' }}>
                                {s.name || s}
                            </option>
                        ))}
                    </select>
                    {canManageSettings && (
                        <button
                            onClick={() => setIsStatusModalOpen(true)}
                            className="p-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition"
                        >
                            <Settings size={16} />
                        </button>
                    )}
                </div>
            </div>
            <StatusManagementModal isOpen={isStatusModalOpen} onClose={() => setIsStatusModalOpen(false)} />

            {/* PRODUCT TYPES (New) */}
            <div className="space-y-0.5">
                <label className="text-xs font-medium text-stone-500 uppercase">PRODUCT TYPES:</label>
                <div className="flex flex-wrap gap-2">
                    {PRODUCT_TYPES.filter(t => t !== 'normal').map(type => (
                        <label key={type} className={`
                            flex items-center justify-center px-2 py-1.5 rounded border transition-all cursor-pointer select-none
                            ${(productData.product_types || []).includes(type)
                                ? 'bg-blue-900/30 border-blue-600 text-blue-200'
                                : 'bg-stone-950 border-stone-700 text-stone-400 hover:bg-stone-900'
                            }
                            ${type === 'accessory' && (productData.product_types || []).includes('component') ? 'opacity-50 cursor-not-allowed' : ''}
                        `}>
                            <input
                                type="checkbox"
                                checked={(productData.product_types || []).includes(type)}
                                onChange={() => handleTypeChange(type)}
                                disabled={!canEditContent || (type === 'accessory' && (productData.product_types || []).includes('component'))}
                                className="hidden"
                            />
                            <span className="text-[10px] font-normal uppercase">{type.replace('_', ' ')}</span>
                        </label>
                    ))}
                </div>
            </div>

            {/* STOCK LEVEL */}
            <div className="space-y-0.5">
                <label className="text-xs font-medium text-stone-500 uppercase">STOCK LEVEL:</label>
                <div className="flex gap-2">
                    <Input
                        name="stock"
                        type="number"
                        value={productData.stock}
                        onChange={handleChange}
                        className="bg-stone-950 border-stone-700 flex-1 py-1.5"
                        disabled={!canEditContent}
                    />

                    <button className="p-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition">
                        <RefreshCw size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};
